import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, FileText, RefreshCw, Send, Copy, Trash2, X, Edit3, DollarSign,
  Eye, ExternalLink, CheckCircle2, Upload as UploadIcon, Download,
  MessageSquare, Undo2, Square as SquareIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminListInvoices, adminGetInvoice, adminCreateInvoice, adminUpdateInvoice,
  adminDeleteInvoice, adminSendInvoice, adminDuplicateInvoice, adminCancelInvoice,
  adminMarkInvoicePaid, adminAddInvoiceFile, adminDeleteInvoiceFile,
  adminInvoicePdfUrl, listReadyMade, listProducts,
  adminInvoiceResend, adminInvoiceRefund, apiClient,
} from "../../lib/api";

const STATUS_TABS = [
  { k: "all", l: "All" }, { k: "draft", l: "Draft" }, { k: "sent", l: "Sent" },
  { k: "viewed", l: "Viewed" }, { k: "approved", l: "Approved" },
  { k: "partially_paid", l: "Partial" }, { k: "paid", l: "Paid" },
  { k: "cancelled", l: "Cancelled" },
];const STATUS_CLS = {
  draft: "text-zinc-500", sent: "text-amber-400", viewed: "text-amber-300",
  approved: "text-emerald-400", partially_paid: "text-amber-400",
  paid: "text-emerald-400", cancelled: "text-zinc-500",
  converted_to_order: "text-emerald-400", overdue: "text-red-400",
};

const money = (v) => `$${(Number(v) || 0).toFixed(2)}`;

/** Top-level Admin → Invoices tab. */
export default function InvoicesTab({ pw }) {
  const [subtab, setSubtab] = useState("list");  // "list" | "square"
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // invoice doc being edited, or {} for new
  const [marking, setMarking] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (search.trim()) params.q = search.trim();
      setRows(await adminListInvoices(pw, params));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load invoices");
    } finally { setLoading(false); }
  }, [pw, status, search]);

  useEffect(() => { reload(); }, [reload]);

  // Cross-tab handoff — OrdersTab puts the new invoice id here when the
  // admin clicks "Create Reorder Invoice", then jumps to this tab.
  useEffect(() => {
    const pending = sessionStorage.getItem("tc_admin_open_invoice");
    if (pending) {
      sessionStorage.removeItem("tc_admin_open_invoice");
      openEdit(pending).catch(() => toast.error("Could not open invoice"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => {
    const out = { count: rows.length, paid: 0, due: 0 };
    rows.forEach((r) => {
      out.paid += Number(r.amount_paid) || 0;
      // Skip cancelled / refunded — those balances are no longer owed.
      if (r.status === "cancelled" || r.status === "refunded") return;
      out.due += Number(r.balance_due) || 0;
    });
    return out;
  }, [rows]);

  const openNew = () => setEditing({});
  const openEdit = async (iid) => {
    const fresh = await adminGetInvoice(iid, pw);
    setEditing(fresh);
  };

  const onAction = async (kind, row) => {
    try {
      if (kind === "send") { await adminSendInvoice(row.id, pw); toast.success("Invoice emailed"); }
      else if (kind === "duplicate") { const d = await adminDuplicateInvoice(row.id, pw); toast.success(`Created ${d.number}`); }
      else if (kind === "cancel") {
        if (!window.confirm(`Cancel invoice ${row.number}?`)) return;
        await adminCancelInvoice(row.id, pw); toast.success("Cancelled");
      }
      else if (kind === "delete") {
        if (!window.confirm(`Hard-delete draft ${row.number}? This cannot be undone.`)) return;
        await adminDeleteInvoice(row.id, pw); toast.success("Deleted");
      }
      else if (kind === "resend") { await adminInvoiceResend(row.id, pw); toast.success("Reminder resent"); }
      else if (kind === "whatsapp") {
        const phone = (row.customer_phone || "").replace(/\D/g, "");
        if (!phone) { toast.error("No customer phone on file"); return; }
        const url = `${window.location.origin}/invoice/${row.public_token}`;
        const text = encodeURIComponent(`Hey ${row.customer_name || ""}, your invoice ${row.number} is ready: ${url}`);
        window.open(`https://wa.me/${phone}?text=${text}`, "_blank");
      }
      else if (kind === "refund") {
        const max = (row.amount_paid || 0).toFixed(2);
        const amt = window.prompt(`Refund amount (max $${max}):`, max);
        if (!amt) return;
        const ref = window.prompt("Reference / reason (optional):", "") || "";
        await adminInvoiceRefund(row.id, { amount: Number(amt), reference: ref }, pw);
        toast.success("Refund recorded");
      }
      else if (kind === "copyLink") {
        const url = `${window.location.origin}/invoice/${row.public_token}`;
        await navigator.clipboard.writeText(url);
        toast.success("Payment link copied");
      }
      else if (kind === "pdf") {
        window.open(adminInvoicePdfUrl(row.id, pw), "_blank");
      }
      else if (kind === "squareManualPaidDone") {
        // Fire-and-forget — the new tab has already opened via the <a>
        // click. This just dismisses the amber "Mark paid on Square"
        // reminder banner in our admin UI.
        await apiClient.post(
          `/admin/invoices/${row.id}/square-manual-paid-done`,
          {}, { headers: { "X-Admin-Password": pw } },
        );
        toast.success("Reminder dismissed — Square Dashboard opened in new tab");
      }
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div data-testid="invoices-tab">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-3xl uppercase flex items-center gap-3">
            <FileText size={22} className="text-[var(--tc-red)]" /> Invoices
          </h2>
          <div className="font-mono text-[11px] text-zinc-500 mt-1">
            {totals.count} invoice{totals.count === 1 ? "" : "s"} · paid {money(totals.paid)} · due <span className="text-[var(--tc-red)]">{money(totals.due)}</span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
                 data-testid="invoice-search"
                 className="bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 text-xs outline-none" />
          <button onClick={reload} className="btn-tc-secondary text-xs flex items-center gap-2"><RefreshCw size={12} /> Refresh</button>
          <button data-testid="create-invoice-btn" onClick={openNew} className="btn-tc-primary text-xs flex items-center gap-2"><Plus size={12} /> New Invoice</button>
        </div>
      </div>

      {/* Subtabs — Invoices list vs. Square Dashboard reconciliation shortcut */}
      <div className="flex gap-1.5 mb-4 border-b border-[#222]">
        <button
          data-testid="invoices-subtab-list"
          onClick={() => setSubtab("list")}
          className={`px-4 py-2 font-mono text-[11px] tracking-widest uppercase border-b-2 -mb-px transition-colors ${
            subtab === "list"
              ? "border-[var(--tc-red)] text-white"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <FileText size={12} className="inline mr-2" />Invoices
        </button>
        <button
          data-testid="invoices-subtab-square"
          onClick={() => setSubtab("square")}
          className={`px-4 py-2 font-mono text-[11px] tracking-widest uppercase border-b-2 -mb-px transition-colors ${
            subtab === "square"
              ? "border-[var(--tc-red)] text-white"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <SquareIcon size={12} className="inline mr-2" />Square Dashboard
        </button>
      </div>

      {subtab === "square" ? (
        <SquareDashboardPanel pw={pw} onAction={onAction} />
      ) : (
      <>
      <div className="flex gap-1.5 flex-wrap mb-4">
        {STATUS_TABS.map((t) => (
          <button key={t.k} data-testid={`invoice-status-${t.k}`} onClick={() => setStatus(t.k)}
                  className={`px-3 py-1.5 border font-mono text-[10px] tracking-widest uppercase ${
                    status === t.k ? "bg-[var(--tc-red)] border-[var(--tc-red)] text-white" : "border-[#222] text-zinc-400 hover:text-white"
                  }`}>{t.l}</button>
        ))}
      </div>

      {loading ? <div className="text-zinc-500 font-mono text-sm">Loading…</div>
       : rows.length === 0 ? (
        <div className="surface-card p-12 text-center">
          <FileText size={32} className="mx-auto mb-3 text-zinc-700" />
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest mb-3">No invoices yet.</div>
          <button onClick={openNew} className="btn-tc-primary text-xs">Create your first invoice</button>
        </div>
      ) : (
        <div className="surface-card overflow-x-auto" data-testid="invoices-grid">
          <table className="w-full text-sm">
            <thead className="bg-[#050505] border-b border-[#222]">
              <tr className="font-mono text-[10px] tracking-widest text-zinc-500">
                <th className="p-3 text-left">NUMBER</th>
                <th className="p-3 text-left">CUSTOMER</th>
                <th className="p-3 text-right">TOTAL</th>
                <th className="p-3 text-right">PAID</th>
                <th className="p-3 text-right">DUE</th>
                <th className="p-3 text-left">STATUS</th>
                <th className="p-3 text-left">VIEWED</th>
                <th className="p-3 text-left">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#222] hover:bg-[#0a0a0a]" data-testid={`invoice-${r.id}`}>
                  <td className="p-3 font-mono font-bold">
                    <button onClick={() => openEdit(r.id)} className="hover:text-[var(--tc-red)]">{r.number}</button>
                  </td>
                  <td className="p-3">
                    <div className="font-bold">{r.customer_name || "—"}</div>
                    <div className="text-[10px] text-zinc-500">{r.customer_email}</div>
                  </td>
                  <td className="p-3 text-right font-mono">{money(r.total)}</td>
                  <td className="p-3 text-right font-mono text-emerald-400">{money(r.amount_paid)}</td>
                  <td className="p-3 text-right font-mono text-[var(--tc-red)]">{money(r.balance_due)}</td>
                  <td className={`p-3 font-mono text-[10px] tracking-widest uppercase ${STATUS_CLS[r.status] || "text-zinc-300"}`}>{r.status}</td>
                  <td className="p-3 font-mono text-[10px] text-zinc-500">
                    {r.view_count > 0 ? <><Eye size={10} className="inline mr-1" />{r.view_count}× · {new Date(r.last_viewed_at).toLocaleDateString()}</> : "—"}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 flex-wrap">
                      <button data-testid={`invoice-edit-${r.id}`} onClick={() => openEdit(r.id)} className="border border-[#222] hover:border-white text-zinc-300 px-2 py-1 text-[10px]" title="Edit"><Edit3 size={11} /></button>
                      <button data-testid={`invoice-send-${r.id}`} onClick={() => onAction("send", r)} className="border border-[#222] hover:border-emerald-700 text-emerald-300 px-2 py-1 text-[10px]" title="Send"><Send size={11} /></button>
                      <button onClick={() => onAction("copyLink", r)} className="border border-[#222] hover:border-white text-zinc-300 px-2 py-1 text-[10px]" title="Copy link"><Copy size={11} /></button>
                      <button onClick={() => onAction("pdf", r)} className="border border-[#222] hover:border-white text-zinc-300 px-2 py-1 text-[10px]" title="PDF"><Download size={11} /></button>
                      {r.customer_phone && (
                        <button data-testid={`invoice-whatsapp-${r.id}`} onClick={() => onAction("whatsapp", r)} className="border border-emerald-700 hover:bg-emerald-950 text-emerald-300 px-2 py-1 text-[10px]" title="Send via WhatsApp"><MessageSquare size={11} /></button>
                      )}
                      {["sent","viewed","approved","partially_paid","overdue"].includes(r.status) && (
                        <button data-testid={`invoice-resend-${r.id}`} onClick={() => onAction("resend", r)} className="border border-[#222] hover:border-white text-zinc-300 px-2 py-1 text-[10px]" title="Resend reminder"><RefreshCw size={11} /></button>
                      )}
                      {r.amount_paid > 0 && r.status !== "refunded" && (
                        <button data-testid={`invoice-refund-${r.id}`} onClick={() => onAction("refund", r)} className="border border-red-900 hover:bg-red-950 text-red-300 px-2 py-1 text-[10px]" title="Record refund"><Undo2 size={11} /></button>
                      )}
                      {r.balance_due > 0 && (
                        <button data-testid={`invoice-mark-paid-${r.id}`} onClick={() => setMarking(r)} className="border border-emerald-700 hover:bg-emerald-950 text-emerald-300 px-2 py-1 text-[10px]" title="Mark paid"><DollarSign size={11} /></button>
                      )}
                      {r.square_manual_paid_pending && r.square_dashboard_url && (
                        <a href={r.square_dashboard_url} target="_blank" rel="noreferrer"
                          data-testid={`invoice-square-mark-paid-${r.id}`}
                          onClick={() => onAction("squareManualPaidDone", r)}
                          className="border border-amber-500 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 px-2 py-1 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1"
                          title="Open in Square Dashboard to mark this invoice PAID natively">
                          <ExternalLink size={11} /> Mark paid on Square
                        </a>
                      )}
                      <a href={`/invoice/${r.public_token}`} target="_blank" rel="noreferrer" className="border border-[#222] hover:border-white text-zinc-300 px-2 py-1 text-[10px]" title="Open customer view"><ExternalLink size={11} /></a>
                      {r.status === "draft" && (
                        <button onClick={() => onAction("delete", r)} data-testid={`invoice-delete-${r.id}`} className="border border-red-900 hover:bg-red-950 text-red-300 px-2 py-1 text-[10px]" title="Delete draft"><Trash2 size={11} /></button>
                      )}
                      {!["paid", "cancelled", "draft", "converted_to_order", "refunded"].includes(r.status) && (
                        <button onClick={() => onAction("cancel", r)} data-testid={`invoice-cancel-${r.id}`} className="border border-red-900 hover:bg-red-950 text-red-300 px-2 py-1 text-[10px]" title="Cancel invoice"><X size={11} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      {editing !== null && (
        <InvoiceEditor
          initial={editing}
          pw={pw}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
      {marking && (
        <MarkPaidModal
          invoice={marking}
          pw={pw}
          onClose={() => setMarking(null)}
          onSaved={() => { setMarking(null); reload(); }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Inline editor — handles create + edit + line items + fees + files
// ============================================================================
function InvoiceEditor({ initial, pw, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState(() => ({
    customer_name: "", customer_email: "", customer_phone: "", billing_address: "",
    line_items: [], files: [], fees: [],
    invoice_discount_amount: 0, invoice_discount_pct: 0,
    tax_rate: 0.13, tax_free: false,
    fulfillment_method: "pickup",
    invoice_due_date: "", production_due_date: "", pickup_date: "",
    internal_notes: "", customer_notes: "",
    etransfer_instructions: "",
    require_approval: true,
    allow_deposit: false, deposit_pct: 50,
    allow_etransfer: true,
    disable_card_payments: false,
    reminders_enabled: true,
    fulfillment_options_enabled: ["pickup", "delivery", "shipping"],
    ...initial,
  }));
  const [saving, setSaving] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);

  // iter89 — dirty tracking + auto-save-on-close for drafts.
  //   `initialJson` captures the form snapshot at mount. If the current
  //   form serializes to anything different AND the invoice is new or in
  //   draft state, we auto-save when the user tries to leave the editor
  //   (cancel button, backdrop click, escape, tab close, browser back).
  //   Sent/paid invoices are NEVER auto-saved to prevent accidental edits.
  const initialJsonRef = useRef(JSON.stringify({}));
  const formRef = useRef(form);
  const savedRef = useRef(false);      // did we run save() at least once?
  const currentIdRef = useRef(initial.id || null);   // updated after first create
  const status = (initial.status || "draft").toLowerCase();
  const eligibleForAutosave = isNew || status === "draft";
  useEffect(() => { formRef.current = form; }, [form]);
  useEffect(() => {
    // Snapshot the form once, right after the initial ...initial spread
    // has been merged into state on first render.
    initialJsonRef.current = JSON.stringify(formRef.current);
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // ---- Line items
  const addLineItem = (defaults = {}) => set({
    line_items: [...form.line_items, {
      id: `li-${Math.random().toString(36).slice(2, 10)}`,
      kind: "custom", name: "", quantity: 1, unit_price: 0,
      discount_amount: 0, discount_pct: 0, taxable: true,
      size: "", color: "", print_location: "", print_size: "",
      artwork_notes: "", production_notes: "",
      ...defaults,
    }],
  });
  const updateLine = (idx, patch) => set({
    line_items: form.line_items.map((li, i) => i === idx ? { ...li, ...patch } : li),
  });
  const removeLine = (idx) => set({ line_items: form.line_items.filter((_, i) => i !== idx) });
  const duplicateLine = (idx) => {
    const li = form.line_items[idx];
    const copy = { ...li, id: `li-${Math.random().toString(36).slice(2, 10)}` };
    const next = [...form.line_items];
    next.splice(idx + 1, 0, copy);
    set({ line_items: next });
  };

  // ---- Fees
  const addFee = () => set({ fees: [...form.fees, { label: "Fee", amount: 0, taxable: true }] });
  const updateFee = (idx, patch) => set({ fees: form.fees.map((f, i) => i === idx ? { ...f, ...patch } : f) });
  const removeFee = (idx) => set({ fees: form.fees.filter((_, i) => i !== idx) });

  // ---- Files
  const onUploadFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Max 8MB");
    const reader = new FileReader();
    reader.onload = async () => {
      if (isNew) {
        set({ files: [...form.files, {
          id: `f-${Math.random().toString(36).slice(2, 10)}`,
          name: file.name, src: reader.result, mime: file.type,
          kind: "mockup", customer_visible: true, uploaded_by: "admin",
        }] });
      } else {
        try {
          const f = await adminAddInvoiceFile(initial.id, {
            name: file.name, src: reader.result, mime: file.type,
            kind: "mockup", customer_visible: true,
          }, pw);
          set({ files: [...form.files, f] });
          toast.success("File added");
        } catch { toast.error("Upload failed"); }
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  const removeFile = async (fid) => {
    if (!isNew) await adminDeleteInvoiceFile(initial.id, fid, pw);
    set({ files: form.files.filter((f) => f.id !== fid) });
  };

  // ---- Computed preview totals (display only)
  const preview = useMemo(() => {
    let subtotal = 0, taxBase = 0;
    form.line_items.forEach((li) => {
      let g = (li.quantity || 0) * (li.unit_price || 0);
      if (li.discount_amount > 0) g -= li.discount_amount;
      if (li.discount_pct > 0) g *= (1 - li.discount_pct / 100);
      g = Math.max(0, g);
      subtotal += g;
      if (li.taxable && !form.tax_free) taxBase += g;
    });
    let invDisc = (form.invoice_discount_amount || 0) + subtotal * (form.invoice_discount_pct || 0) / 100;
    invDisc = Math.min(invDisc, subtotal);
    if (subtotal > 0 && invDisc > 0) taxBase *= (1 - invDisc / subtotal);
    let feesT = 0, feesTax = 0;
    form.fees.forEach((f) => { feesT += Number(f.amount) || 0; if (f.taxable && !form.tax_free) feesTax += Number(f.amount) || 0; });
    const tax = form.tax_free ? 0 : (taxBase + feesTax) * (form.tax_rate || 0);
    return {
      subtotal: subtotal.toFixed(2),
      discount: invDisc.toFixed(2),
      fees: feesT.toFixed(2),
      tax: tax.toFixed(2),
      total: (Math.max(0, subtotal - invDisc) + feesT + tax).toFixed(2),
    };
  }, [form]);

  // Returns true if the current form has "meaningful" content worth
  // persisting as a draft (empty forms aren't auto-saved so we don't
  // create phantom rows every time the operator opens & closes the modal).
  const hasMeaningfulContent = useCallback(() => {
    const f = formRef.current;
    if ((f.customer_email || "").trim() || (f.customer_name || "").trim()) return true;
    if ((f.line_items || []).length > 0) return true;
    if ((f.customer_notes || "").trim() || (f.internal_notes || "").trim()) return true;
    return false;
  }, []);

  const isDirty = useCallback(() => {
    return JSON.stringify(formRef.current) !== initialJsonRef.current;
  }, []);

  const save = async ({ silent = false } = {}) => {
    if (!silent) {
      if (!form.customer_email && !form.customer_name) {
        return toast.error("Customer name or email required");
      }
    } else if (!hasMeaningfulContent()) {
      // Nothing to save — quietly bail.
      return;
    }
    setSaving(true);
    try {
      const body = { ...formRef.current };
      // Strip ISO date suffix that <input type=date> uses ("YYYY-MM-DD")
      ["invoice_due_date", "production_due_date", "pickup_date"].forEach((k) => {
        if (body[k] && !body[k].includes("T")) body[k] = `${body[k]}T00:00:00Z`;
      });
      const editingId = currentIdRef.current;
      const saved = editingId
        ? await adminUpdateInvoice(editingId, body, pw)
        : await adminCreateInvoice(body, pw);
      // Update the id in the ref so subsequent auto-saves in this session
      // become updates instead of a second create.
      currentIdRef.current = saved.id || editingId;
      // Refresh the initial snapshot so we don't re-trigger a "still dirty"
      // auto-save right after this succeeds.
      initialJsonRef.current = JSON.stringify(formRef.current);
      savedRef.current = true;
      if (!silent) {
        toast.success(editingId ? "Invoice saved as draft" : `Draft created — ${saved.number}`);
        onSaved();
      } else {
        // Background auto-save; a quieter tap for feedback without
        // interrupting the operator's workflow.
        toast.info(`Draft auto-saved${saved.number ? ` — ${saved.number}` : ""}`);
      }
      return saved;
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // iter89 — Auto-save-on-leave. Fires when:
  //   1. The user closes the modal (Cancel / backdrop / Escape)
  //   2. The tab is being closed/refreshed (beforeunload → best-effort
  //      sync attempt via keepalive fetch)
  //   3. This component unmounts for any other reason (parent re-render)
  // Only fires for new or draft invoices with meaningful content.
  const doAutoSave = useCallback(() => {
    if (!eligibleForAutosave) return;
    if (!isDirty()) return;
    if (!hasMeaningfulContent()) return;
    // Fire & forget — we cannot await inside a beforeunload/unmount handler.
    save({ silent: true });
  }, [eligibleForAutosave, hasMeaningfulContent, isDirty]);

  useEffect(() => {
    // Warn on tab close if unsaved. beforeunload cannot show custom text
    // in modern browsers but the browser's native "leave?" dialog appears.
    const onBeforeUnload = (e) => {
      if (!eligibleForAutosave || !isDirty() || !hasMeaningfulContent()) return;
      // Kick off a keepalive save so the draft actually persists even if
      // the operator confirms "leave".
      doAutoSave();
      e.preventDefault();
      e.returnValue = ""; // required for Chrome to show the prompt
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      // Best-effort auto-save when the modal is unmounting.
      doAutoSave();
    };
  }, [doAutoSave, eligibleForAutosave, hasMeaningfulContent, isDirty]);

  const handleClose = () => {
    if (eligibleForAutosave && isDirty() && hasMeaningfulContent() && !saving) {
      // Fire silent auto-save; onSaved refreshes the list once it lands.
      save({ silent: true }).then(() => {
        onSaved?.();
        onClose();
      }).catch(() => onClose());
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-2 overflow-y-auto" onClick={handleClose}>
      <div onClick={(e) => e.stopPropagation()} className="surface-card max-w-5xl w-full my-4 max-h-[95vh] overflow-y-auto" data-testid="invoice-editor">
        <div className="p-5 border-b border-[#222] flex justify-between items-center sticky top-0 bg-[#0A0A0A] z-10">
          <h3 className="font-display text-2xl uppercase">
            {isNew ? "New Invoice" : `Edit ${initial.number}`}
          </h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Customer block */}
          <fieldset className="border border-[#222] p-4">
            <legend className="font-mono text-[10px] tracking-widest text-zinc-500 px-2">CUSTOMER</legend>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Name" value={form.customer_name} onChange={(v) => set({ customer_name: v })} testId="inv-customer-name" />
              <Field label="Email" type="email" value={form.customer_email} onChange={(v) => set({ customer_email: v })} testId="inv-customer-email" />
              <Field label="Phone" value={form.customer_phone} onChange={(v) => set({ customer_phone: v })} testId="inv-customer-phone" />
              <Field label="Billing Address" value={form.billing_address} onChange={(v) => set({ billing_address: v })} testId="inv-billing-address" />
            </div>
          </fieldset>

          {/* Line items */}
          <fieldset className="border border-[#222] p-4">
            <legend className="font-mono text-[10px] tracking-widest text-zinc-500 px-2">LINE ITEMS</legend>
            <div className="space-y-2">
              {form.line_items.map((li, idx) => (
                <div key={li.id} className="border border-[#222] p-3 bg-[#050505]" data-testid={`line-${idx}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <input className="col-span-5 bg-black border border-[#222] px-2 py-1.5 text-sm" placeholder="Item name *" value={li.name} onChange={(e) => updateLine(idx, { name: e.target.value })} data-testid={`line-${idx}-name`} />
                    <input className="col-span-2 bg-black border border-[#222] px-2 py-1.5 text-sm" type="number" placeholder="Qty" value={li.quantity} onChange={(e) => updateLine(idx, { quantity: Number(e.target.value) || 0 })} data-testid={`line-${idx}-qty`} />
                    <input className="col-span-2 bg-black border border-[#222] px-2 py-1.5 text-sm" type="number" step="0.01" placeholder="Unit $" value={li.unit_price} onChange={(e) => updateLine(idx, { unit_price: Number(e.target.value) || 0 })} data-testid={`line-${idx}-price`} />
                    <input className="col-span-2 bg-black border border-[#222] px-2 py-1.5 text-sm" type="number" step="0.01" placeholder="Disc $" value={li.discount_amount} onChange={(e) => updateLine(idx, { discount_amount: Number(e.target.value) || 0 })} title="Line discount in $" />
                    <button onClick={() => removeLine(idx)} className="col-span-1 text-zinc-500 hover:text-[var(--tc-red)]" title="Remove"><Trash2 size={14} /></button>
                  </div>
                  <div className="grid grid-cols-12 gap-2 mt-2">
                    <input className="col-span-2 bg-black border border-[#222] px-2 py-1 text-xs" placeholder="Size" value={li.size || ""} onChange={(e) => updateLine(idx, { size: e.target.value })} />
                    <input className="col-span-2 bg-black border border-[#222] px-2 py-1 text-xs" placeholder="Color" value={li.color || ""} onChange={(e) => updateLine(idx, { color: e.target.value })} />
                    <input className="col-span-3 bg-black border border-[#222] px-2 py-1 text-xs" placeholder="Print location" value={li.print_location || ""} onChange={(e) => updateLine(idx, { print_location: e.target.value })} />
                    <input className="col-span-2 bg-black border border-[#222] px-2 py-1 text-xs" placeholder="Print size" value={li.print_size || ""} onChange={(e) => updateLine(idx, { print_size: e.target.value })} />
                    <label className="col-span-2 flex items-center gap-1 font-mono text-[10px] text-zinc-400">
                      <input type="checkbox" checked={li.taxable} onChange={(e) => updateLine(idx, { taxable: e.target.checked })} className="accent-[var(--tc-red)]" />
                      Taxable
                    </label>
                    <button onClick={() => duplicateLine(idx)} className="col-span-1 text-zinc-500 hover:text-white" title="Duplicate"><Copy size={12} /></button>
                  </div>
                  <input className="w-full mt-2 bg-black border border-[#222] px-2 py-1 text-xs" placeholder="Artwork / production notes" value={li.artwork_notes || ""} onChange={(e) => updateLine(idx, { artwork_notes: e.target.value })} />
                </div>
              ))}
              <div className="flex gap-2">
                <button data-testid="add-line-custom" onClick={() => addLineItem()} className="btn-tc-secondary text-xs flex items-center gap-1"><Plus size={11} /> Custom Item</button>
                <button data-testid="add-line-product" onClick={() => setShowProductPicker(true)} className="btn-tc-secondary text-xs flex items-center gap-1"><Plus size={11} /> From Catalog</button>
              </div>
            </div>
          </fieldset>

          {/* Fees */}
          <fieldset className="border border-[#222] p-4">
            <legend className="font-mono text-[10px] tracking-widest text-zinc-500 px-2">FEES & DISCOUNTS</legend>
            <div className="space-y-2 mb-3">
              {form.fees.map((f, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2">
                  <input className="col-span-7 bg-black border border-[#222] px-2 py-1.5 text-sm" placeholder="Fee label" value={f.label} onChange={(e) => updateFee(idx, { label: e.target.value })} />
                  <input className="col-span-3 bg-black border border-[#222] px-2 py-1.5 text-sm" type="number" step="0.01" placeholder="Amount" value={f.amount} onChange={(e) => updateFee(idx, { amount: Number(e.target.value) || 0 })} />
                  <label className="col-span-1 flex items-center text-[10px] font-mono text-zinc-400">
                    <input type="checkbox" checked={f.taxable} onChange={(e) => updateFee(idx, { taxable: e.target.checked })} className="accent-[var(--tc-red)]" /> Tax
                  </label>
                  <button onClick={() => removeFee(idx)} className="col-span-1 text-zinc-500 hover:text-[var(--tc-red)]"><Trash2 size={12} /></button>
                </div>
              ))}
              <button data-testid="add-fee" onClick={addFee} className="btn-tc-secondary text-xs flex items-center gap-1"><Plus size={11} /> Add Fee</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Invoice discount ($)" type="number" step="0.01" value={form.invoice_discount_amount} onChange={(v) => set({ invoice_discount_amount: Number(v) || 0 })} />
              <Field label="Invoice discount (%)" type="number" step="0.01" value={form.invoice_discount_pct} onChange={(v) => set({ invoice_discount_pct: Number(v) || 0 })} />
            </div>
          </fieldset>

          {/* Tax + Payment */}
          <fieldset className="border border-[#222] p-4">
            <legend className="font-mono text-[10px] tracking-widest text-zinc-500 px-2">TAX & PAYMENT</legend>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Tax rate (decimal, e.g. 0.13)" type="number" step="0.01" value={form.tax_rate} onChange={(v) => set({ tax_rate: Number(v) || 0 })} disabled={form.tax_free} />
              <label className="flex items-center gap-2 font-mono text-xs text-zinc-300">
                <input type="checkbox" checked={form.tax_free} onChange={(e) => set({ tax_free: e.target.checked })} className="accent-[var(--tc-red)]" data-testid="inv-tax-free" />
                TAX-FREE INVOICE
              </label>
              <label className="flex items-center gap-2 font-mono text-xs text-zinc-300">
                <input
                  type="checkbox"
                  checked={form.allow_deposit}
                  onChange={(e) => set({ allow_deposit: e.target.checked })}
                  data-testid="inv-allow-deposit"
                  className="accent-[var(--tc-red)]"
                />
                OFFER DEPOSIT OPTION
              </label>
              {form.allow_deposit && (
                <Field
                  label="Deposit % (rate offered)"
                  type="number"
                  value={form.deposit_pct}
                  onChange={(v) => set({ deposit_pct: Math.max(1, Math.min(99, Number(v) || 50)) })}
                  testId="inv-deposit-pct"
                />
              )}
              <label className="flex items-center gap-2 font-mono text-xs text-zinc-300">
                <input type="checkbox" checked={form.require_approval} onChange={(e) => set({ require_approval: e.target.checked })} className="accent-[var(--tc-red)]" />
                REQUIRE APPROVAL BEFORE PAYMENT
              </label>
              <label className="flex items-center gap-2 font-mono text-xs text-zinc-300">
                <input type="checkbox" checked={form.allow_etransfer} onChange={(e) => set({ allow_etransfer: e.target.checked })} className="accent-[var(--tc-red)]" />
                ALLOW E-TRANSFER
              </label>
              {/* iter90 — sub-option under E-TRANSFER for operators who
                  want the customer to pay ONLY by e-transfer (no Square
                  card capture on the public invoice page). Applies to
                  the invoice's own pay page; Square-hosted checkout
                  ignores this flag. */}
              <label
                className={`flex items-center gap-2 font-mono text-xs pl-6 ${form.allow_etransfer ? "text-zinc-300" : "text-zinc-600"}`}
                data-testid="inv-disable-card"
              >
                <input
                  type="checkbox"
                  checked={!!form.disable_card_payments}
                  onChange={(e) => set({ disable_card_payments: e.target.checked })}
                  className="accent-[var(--tc-red)]"
                  disabled={!form.allow_etransfer}
                />
                └ DISABLE CREDIT CARD PAYMENTS (E-TRANSFER ONLY)
              </label>
              <label className="flex items-center gap-2 font-mono text-xs text-zinc-300">
                <input type="checkbox" checked={form.reminders_enabled} onChange={(e) => set({ reminders_enabled: e.target.checked })} className="accent-[var(--tc-red)]" data-testid="inv-reminders-enabled" />
                AUTO REMINDERS (1d / 3d / 7d / due)
              </label>
            </div>
          </fieldset>

          {/* Dates + Fulfillment */}
          <fieldset className="border border-[#222] p-4">
            <legend className="font-mono text-[10px] tracking-widest text-zinc-500 px-2">DATES & FULFILLMENT</legend>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Payment due" type="date" value={(form.invoice_due_date || "").slice(0, 10)} onChange={(v) => set({ invoice_due_date: v })} />
              <Field label="Production due" type="date" value={(form.production_due_date || "").slice(0, 10)} onChange={(v) => set({ production_due_date: v })} />
              <Field label="Pickup date" type="date" value={(form.pickup_date || "").slice(0, 10)} onChange={(v) => set({ pickup_date: v })} />
              <div>
                <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">FULFILLMENT METHOD</div>
                <select value={form.fulfillment_method} onChange={(e) => set({ fulfillment_method: e.target.value })} className="w-full bg-black border border-[#222] px-2 py-2 text-sm">
                  <option value="pickup">Pickup</option>
                  <option value="delivery">Local Delivery</option>
                  <option value="shipping">Shipping</option>
                </select>
              </div>
            </div>
          </fieldset>

          {/* Files */}
          <fieldset className="border border-[#222] p-4">
            <legend className="font-mono text-[10px] tracking-widest text-zinc-500 px-2">FILES / MOCKUPS</legend>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
              {form.files.map((f) => (
                <div key={f.id} className="relative aspect-square border border-[#222] bg-[#050505] overflow-hidden group">
                  {f.mime?.startsWith("image/") ? <img src={f.src} alt={f.name} className="w-full h-full object-contain" /> : <div className="w-full h-full flex items-center justify-center font-mono text-[9px] text-zinc-500 p-1 text-center">{f.name}</div>}
                  <button onClick={() => removeFile(f.id)} className="absolute top-1 right-1 bg-black/80 border border-[#444] text-zinc-300 w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100"><X size={10} /></button>
                  {!f.customer_visible && <div className="absolute bottom-0 inset-x-0 bg-black/80 text-[8px] font-mono text-amber-400 text-center py-0.5">INTERNAL</div>}
                </div>
              ))}
              <label className="aspect-square border border-dashed border-[#444] hover:border-white flex items-center justify-center cursor-pointer">
                <input type="file" accept="image/*,.pdf,.svg,.ai,.eps" onChange={onUploadFile} className="sr-only" />
                <UploadIcon size={18} className="text-zinc-500" />
              </label>
            </div>
          </fieldset>

          {/* Notes */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Customer-visible notes" textarea value={form.customer_notes} onChange={(v) => set({ customer_notes: v })} testId="inv-customer-notes" />
            <Field label="Internal admin notes (NOT shown to customer)" textarea value={form.internal_notes} onChange={(v) => set({ internal_notes: v })} testId="inv-internal-notes" />
          </div>
          <Field label="E-transfer instructions (uses default if blank)" textarea value={form.etransfer_instructions} onChange={(v) => set({ etransfer_instructions: v })} testId="inv-etransfer" />

          {/* Preview totals — iter90: order requested by operator is
              Discount → Subtotal → Tax → Fees → Total. */}
          <div className="surface-card p-4 max-w-sm ml-auto font-mono text-sm">
            <div className="flex justify-between py-1"><span className="text-zinc-400">Discount</span><span className="text-[var(--tc-red)]">-${preview.discount}</span></div>
            <div className="flex justify-between py-1"><span className="text-zinc-400">Subtotal</span><span>${preview.subtotal}</span></div>
            <div className="flex justify-between py-1"><span className="text-zinc-400">Tax</span><span>${preview.tax}</span></div>
            <div className="flex justify-between py-1"><span className="text-zinc-400">Fees</span><span>${preview.fees}</span></div>
            <div className="flex justify-between py-2 border-t border-[#333] mt-1 font-bold text-base"><span>Preview Total</span><span data-testid="invoice-preview-total">${preview.total}</span></div>
          </div>
        </div>

        <div className="p-5 border-t border-[#222] flex justify-end gap-2 sticky bottom-0 bg-[#0A0A0A]">
          {/* iter89 — dirty indicator so the operator sees they have
              unsaved changes at a glance. */}
          {eligibleForAutosave && isDirty() && hasMeaningfulContent() && (
            <div
              className="mr-auto font-mono text-[10px] tracking-widest text-amber-400 flex items-center"
              data-testid="invoice-dirty-indicator"
            >
              · UNSAVED CHANGES · WILL AUTO-SAVE ON CLOSE
            </div>
          )}
          <button onClick={handleClose} className="btn-tc-secondary" data-testid="cancel-invoice-btn">Close</button>
          <button
            data-testid="save-invoice-btn"
            onClick={() => save({ silent: false })}
            disabled={saving}
            className="btn-tc-primary disabled:opacity-50"
          >
            {saving
              ? "Saving…"
              : eligibleForAutosave
                ? (isNew ? "Save as Draft" : "Save Draft")
                : "Save Changes"}
          </button>
        </div>
      </div>

      {showProductPicker && (
        <ProductPicker onClose={() => setShowProductPicker(false)} onPick={(item) => { addLineItem(item); setShowProductPicker(false); }} />
      )}
    </div>
  );
}

// ============================================================================
// Helper components
// ============================================================================
function Field({ label, value, onChange, type = "text", step, textarea, disabled, testId }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-widest text-zinc-500">{label.toUpperCase()}</span>
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3}
                  data-testid={testId}
                  className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 mt-1 text-sm outline-none resize-y" />
      ) : (
        <input type={type} step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled}
               data-testid={testId}
               className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 mt-1 text-sm outline-none disabled:opacity-40 [color-scheme:dark]" />
      )}
    </label>
  );
}

function ProductPicker({ onClose, onPick }) {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    Promise.all([listProducts(), listReadyMade()])
      .then(([custom, ready]) => {
        setProducts([
          ...(custom || []).map((p) => ({ ...p, _type: "custom" })),
          ...(ready || []).map((p) => ({ ...p, _type: "ready_made" })),
        ]);
      });
  }, []);
  const filtered = products.filter((p) => !q.trim() || p.name?.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="fixed inset-0 z-[60] bg-black/95 p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="max-w-3xl mx-auto surface-card" data-testid="product-picker">
        <div className="p-4 border-b border-[#222] flex items-center gap-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" autoFocus className="flex-1 bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 text-sm outline-none" />
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-4 max-h-[60vh] overflow-y-auto">
          {filtered.slice(0, 60).map((p) => (
            <button
              key={`${p._type}-${p.id}`}
              data-testid={`pick-product-${p.id}`}
              onClick={() => onPick({
                kind: "product", product_id: p.id, product_type: p._type,
                name: p.name, unit_price: p.base_price || p.price || 0,
                description: p.description || "",
              })}
              className="surface-card p-2 hover:border-[var(--tc-red)] text-left flex gap-2 items-center"
            >
              <img src={p.image_front || p.image || p.color_images?.[Object.keys(p.color_images || {})[0]]} alt={p.name} className="w-12 h-12 object-cover bg-[#050505]" />
              <div className="flex-1 min-w-0">
                <div className="font-ui text-xs font-bold truncate">{p.name}</div>
                <div className="font-mono text-[10px] text-zinc-500">${(p.base_price || p.price || 0).toFixed(2)} · {p._type}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function MarkPaidModal({ invoice, pw, onClose, onSaved }) {
  const [amount, setAmount] = useState(invoice.balance_due);
  const [method, setMethod] = useState("etransfer");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await adminMarkInvoicePaid(invoice.id, { amount: Number(amount), method, reference }, pw);
      toast.success(`Payment recorded · status ${r.invoice.status}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="surface-card max-w-md w-full" data-testid="mark-paid-modal">
        <div className="p-5 border-b border-[#222] flex justify-between items-center">
          <h3 className="font-display text-2xl uppercase">Record Payment</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Amount (CAD)" type="number" step="0.01" value={amount} onChange={setAmount} testId="markpaid-amount" />
          <div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">METHOD</div>
            <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="markpaid-method" className="w-full bg-black border border-[#222] px-3 py-2 text-sm">
              <option value="etransfer">E-Transfer</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </select>
          </div>
          <Field label="Reference / note" value={reference} onChange={setReference} testId="markpaid-reference" />
          <div className="font-mono text-[10px] text-zinc-500">
            Total: ${invoice.total.toFixed(2)} · Already paid: ${invoice.amount_paid.toFixed(2)} · Balance: <span className="text-[var(--tc-red)]">${invoice.balance_due.toFixed(2)}</span>
          </div>
        </div>
        <div className="p-5 border-t border-[#222] flex justify-end gap-2">
          <button onClick={onClose} className="btn-tc-secondary">Cancel</button>
          <button data-testid="markpaid-submit" onClick={submit} disabled={busy} className="btn-tc-primary disabled:opacity-50">
            {busy ? "Saving…" : "Record Payment"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// Square Dashboard subtab — persistent shortcut + pending-reconciliation list.
// Locally-paid invoices cannot be marked PAID via Square's public API, so this
// panel gives the operator a one-click launch into Square's Invoices dashboard
// (which uses the browser's existing Square login session) plus a list of
// invoices flagged `square_manual_paid_pending` with per-invoice deep links.
// ============================================================================
const SQUARE_DASHBOARD_URL = "https://app.squareup.com/dashboard/invoices";

function SquareDashboardPanel({ pw, onAction }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [bulking, setBulking] = useState(false);

  const loadPending = useCallback(async () => {
    setLoading(true);
    try {
      const all = await adminListInvoices(pw, {});
      setPending((all || []).filter((r) => r.square_manual_paid_pending));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load pending invoices");
    } finally { setLoading(false); }
  }, [pw]);

  useEffect(() => { loadPending(); }, [loadPending]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pending;
    return pending.filter((r) => (
      (r.number || "").toLowerCase().includes(q)
      || (r.customer_name || "").toLowerCase().includes(q)
      || (r.customer_email || "").toLowerCase().includes(q)
    ));
  }, [pending, query]);

  const copyNumber = async (number) => {
    try {
      await navigator.clipboard.writeText(number);
      toast.success(`Copied ${number}`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const bulkReconcile = async () => {
    if (filtered.length === 0 || bulking) return;
    const scope = query.trim()
      ? `the ${filtered.length} filtered invoice${filtered.length === 1 ? "" : "s"}`
      : `all ${filtered.length} pending invoice${filtered.length === 1 ? "" : "s"}`;
    if (!window.confirm(
      `Mark ${scope} as reconciled?\n\nOnly do this after you've recorded these payments in Square Dashboard. This just dismisses the local reminders.`
    )) return;
    setBulking(true);
    const results = await Promise.allSettled(filtered.map((r) =>
      apiClient.post(
        `/admin/invoices/${r.id}/square-manual-paid-done`,
        {}, { headers: { "X-Admin-Password": pw } },
      ),
    ));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    if (failed === 0) {
      toast.success(`Reconciled ${ok} invoice${ok === 1 ? "" : "s"}`);
    } else {
      toast.error(`Reconciled ${ok} · ${failed} failed`);
    }
    setBulking(false);
    loadPending();
  };

  return (
    <div data-testid="square-dashboard-panel" className="space-y-6">
      {/* Primary shortcut card */}
      <div className="surface-card p-6 border-l-4 border-l-[var(--tc-red)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-[240px]">
            <h3 className="font-display text-2xl uppercase flex items-center gap-2">
              <SquareIcon size={18} className="text-[var(--tc-red)]" /> Open Square Dashboard
            </h3>
            <p className="text-sm text-zinc-400 mt-2 max-w-xl">
              Jump straight into Square&rsquo;s Invoices dashboard to record external
              payments (cash, e-transfer, offline card) for invoices marked paid
              locally. Uses your existing Square browser session &mdash; sign in once
              in Square and this shortcut will land you on the invoices list.
            </p>
          </div>
          <a
            href={SQUARE_DASHBOARD_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="open-square-dashboard-button"
            className="btn-tc-primary text-xs flex items-center gap-2 whitespace-nowrap"
          >
            <ExternalLink size={13} /> Open Square Dashboard
          </a>
        </div>

        <div className="mt-5 border-t border-[#222] pt-4 grid gap-2 font-mono text-[11px] text-zinc-400">
          <div className="flex gap-3">
            <span className="text-[var(--tc-red)] font-bold">1.</span>
            <span>Click <span className="text-white">Open Square Dashboard</span> above (opens in a new tab).</span>
          </div>
          <div className="flex gap-3">
            <span className="text-[var(--tc-red)] font-bold">2.</span>
            <span>Locate the invoice in Square and use <span className="text-white">Add payment</span> to record the external payment (cash / e-transfer / offline card).</span>
          </div>
          <div className="flex gap-3">
            <span className="text-[var(--tc-red)] font-bold">3.</span>
            <span>Return here and click <span className="text-amber-300">Reconciled</span> on the invoice below to dismiss the reminder.</span>
          </div>
        </div>
      </div>

      {/* Pending reconciliation list */}
      <div>
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h4 className="font-mono text-[11px] tracking-widest text-zinc-500 uppercase">
            Awaiting Square Reconciliation
            {!loading && (
              <span className="ml-2 text-amber-400">
                ({query.trim() ? `${filtered.length} / ${pending.length}` : pending.length})
              </span>
            )}
          </h4>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search number, name, email…"
              data-testid="square-pending-search"
              className="bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-1.5 text-xs outline-none w-56"
            />
            {pending.length > 0 && (
              <button
                data-testid="square-pending-bulk-reconcile"
                onClick={bulkReconcile}
                disabled={bulking || filtered.length === 0}
                className="border border-emerald-700 hover:bg-emerald-950 disabled:opacity-40 disabled:cursor-not-allowed text-emerald-300 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1"
                title={query.trim()
                  ? `Mark the ${filtered.length} filtered invoices reconciled`
                  : `Mark all ${pending.length} pending invoices reconciled`}
              >
                <CheckCircle2 size={11} />
                {bulking ? "Reconciling…" : `Mark ${filtered.length} reconciled`}
              </button>
            )}
            <button
              data-testid="square-pending-refresh"
              onClick={loadPending}
              className="btn-tc-secondary text-[10px] flex items-center gap-1"
            >
              <RefreshCw size={11} /> Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-zinc-500 font-mono text-sm">Loading…</div>
        ) : pending.length === 0 ? (
          <div className="surface-card p-10 text-center">
            <CheckCircle2 size={28} className="mx-auto mb-3 text-emerald-500" />
            <div className="font-mono text-xs text-zinc-400 uppercase tracking-widest">
              All caught up &mdash; no invoices awaiting Square reconciliation.
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="surface-card p-10 text-center">
            <div className="font-mono text-xs text-zinc-400 uppercase tracking-widest">
              No pending invoices match &ldquo;{query}&rdquo;.
            </div>
          </div>
        ) : (
          <div className="surface-card overflow-x-auto" data-testid="square-pending-list">
            <table className="w-full text-sm">
              <thead className="bg-[#050505] border-b border-[#222]">
                <tr className="font-mono text-[10px] tracking-widest text-zinc-500">
                  <th className="p-3 text-left">NUMBER</th>
                  <th className="p-3 text-left">CUSTOMER</th>
                  <th className="p-3 text-right">TOTAL</th>
                  <th className="p-3 text-left">LOCAL METHOD</th>
                  <th className="p-3 text-left">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-[#222] hover:bg-[#0a0a0a]"
                      data-testid={`square-pending-${r.id}`}>
                    <td className="p-3 font-mono font-bold">
                      <div className="flex items-center gap-2">
                        <span>{r.number}</span>
                        <button
                          onClick={() => copyNumber(r.number)}
                          data-testid={`square-pending-copy-${r.id}`}
                          title="Copy invoice number (paste into Square search)"
                          className="text-zinc-500 hover:text-white transition-colors"
                        >
                          <Copy size={12} />
                        </button>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="font-bold">{r.customer_name || "—"}</div>
                      <div className="text-[10px] text-zinc-500">{r.customer_email}</div>
                    </td>
                    <td className="p-3 text-right font-mono">{money(r.total)}</td>
                    <td className="p-3 font-mono text-[10px] text-zinc-400 uppercase">
                      {r.square_manual_paid_method || "manual"}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2 flex-wrap">
                        {r.square_dashboard_url && (
                          <a
                            href={r.square_dashboard_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`square-pending-open-${r.id}`}
                            className="border border-amber-500 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 px-2 py-1 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1"
                            title="Open this invoice in Square Dashboard"
                          >
                            <ExternalLink size={11} /> Open in Square
                          </a>
                        )}
                        <button
                          data-testid={`square-pending-reconciled-${r.id}`}
                          onClick={async () => {
                            await onAction("squareManualPaidDone", r);
                            loadPending();
                          }}
                          className="border border-emerald-700 hover:bg-emerald-950 text-emerald-300 px-2 py-1 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1"
                          title="Dismiss reminder — payment recorded in Square"
                        >
                          <CheckCircle2 size={11} /> Reconciled
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
